import { Component, computed, OnInit, signal } from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";
import Swal from "sweetalert2";
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, FormsModule, Validators } from "@angular/forms";
import { CommonModule, DatePipe } from "@angular/common";

import { NgxSpinnerModule } from "ngx-spinner";
import { NgSelectModule } from "@ng-select/ng-select";
import { MatCardModule } from "@angular/material/card";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { MatDividerModule } from "@angular/material/divider";
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { UserService } from "../../../services/user/user.service";
import { ErrorHandlingService } from "../../../services/error-handling/error-handling.service";
import { JsonDataService } from "../../../services/commonData/json-data.service";
import { AuthServiceService } from "../../service/auth-service.service";
import { catchError, of } from "rxjs";
import { ValidationService } from "src/app/services/validations/validation.service";
import { TimerService } from "src/app/services/timer/timer.service";
import { SessionStorageService } from "ngx-webstorage";
import { SessionKeys } from "src/app/shared/enums/session-keys.enum";
import { AuthTokenService } from "src/app/services/auth-token/auth-token.service";
import { ChangePasswordModalComponent } from "src/app/master/user/change-password-modal/change-password-modal.component";
import { MatDialog } from "@angular/material/dialog";
import { HttpService } from "../../../services/http/http.service";


@Component({
  selector: "app-login",
  standalone: true,
  templateUrl: "./login.component.html",
  styleUrls: ["./login.component.css"],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    NgxSpinnerModule,
    NgSelectModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatDividerModule,
    MatProgressSpinnerModule
  ],
})
export class LoginComponent implements OnInit {

  loginForm: FormGroup;
  bln_isPopupOpened = false;

  // SIGNALS
  usernameSignal = signal<string>("");
  passwordSignal = signal<string>("");
  int_flagDisableMessage = signal<boolean>(false);
  str_message = signal<string>("");
  str_warningState = signal<string>("success");
  userResponse = signal<any | null>(null);
  str_messagePlaceholder = 'User ID'; 

  bln_loading = false;
  hidePassword = true;

  logo: any;
  version: any;
  systemName: any;
  int_len_username = 0;
  int_len_password = 0;

  obj_loginResponse: any;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private userService: UserService,
    private jsonData: JsonDataService,
    private authToken: AuthTokenService,
    private errorHandling: ErrorHandlingService,
    private authService: AuthServiceService,
    private sessionStorage: SessionStorageService,
    private dialog: MatDialog,
    private validation: ValidationService
  ) {
    this.loginForm = this.fb.group({
      username: new FormControl("", Validators.compose([this.validation.requiredField])),
      password: new FormControl("", Validators.compose([this.validation.requiredField])),
    });
  }

  ngOnInit(): void {
    this.loadJSONConfig();
    this.handleTruncateAllParam();
  }

  /* ---------------- CONFIG ---------------- */

  loadJSONConfig() {
    this.bln_loading = true;
    this.jsonData.getValueFromJSON()
      .then((res: any) => {
        this.bln_loading = false;
        if (!res) return;

        const projectName = res.ProjectName?.[0]?.Name;
        const logoName = res.logoChanges?.[1]?.logoLogin;

        this.logo = `assets/images/${projectName}/${logoName}`;
        this.version = res.logoChanges?.[3]?.version;
        this.systemName = res.logoChanges?.[4]?.systemName;

        this.int_len_username =
          res.InputField.find((x: any) => x.Name === "WindowsUserName")?.Value || 0;
        this.int_len_password =
          res.InputField.find((x: any) => x.Name === "PasswordMaxLength")?.Value || 0;
      })
      .catch(() => {
        this.bln_loading = false;
        this.errorHandling.checkError(500);
      });
  }

  handleTruncateAllParam() {
    if (this.route.snapshot.queryParams["TruncateAll"] == 1) {
      this.bln_loading = true;
      this.authService.truncateAll().subscribe((res: any) => {
        this.bln_loading = false;
        res.status === "Success"
          ? this.router.navigate(["authentication/login"])
          : this.errorHandling.checkError(500);
      });
    }
  }

  /* ---------------- GETTERS ---------------- */

  get username() { return this.loginForm.get("username"); }
  get password() { return this.loginForm.get("password"); }

  /* ---------------- LOGIN (NLDAP ONLY) ---------------- */

  onFormSubmit() {
    this.usernameSignal.set(this.loginForm.value.username?.trim());
    this.passwordSignal.set(this.loginForm.value.password?.trim());

    if (!this.usernameSignal() || !this.passwordSignal()) {
      this.showValidationMsg();
      return;
    }

    this.nldapSubmit();
  }

  nldapSubmit() {
    const username = this.usernameSignal();
    const password = this.passwordSignal();

    const payload = {
      userId: username,
      userPass: password,
      source: "SOFTWARE"
    };

    this.bln_loading = true;

    this.authService.loginNLDAP(payload).pipe(
      catchError(err => of({ error: err }))
    ).subscribe((res: any) => {

      this.bln_loading = false;

      if (res?.error) {
        this.errorHandling.checkError(res.error.status || 500);
        return;
      }

      this.authToken.storeTokenToLocal(res.result.accessToken);
      this.obj_loginResponse = res.result;

      if (typeof this.obj_loginResponse?.result === "object") {
        this.userService.storeUserDetails(this.obj_loginResponse);
        const rights = this.sessionStorage?.retrieve(SessionKeys.RightsArray);


        console.log("Login rights Added ✔");
        console.log(rights)

        const lastRoute = this.sessionStorage.retrieve(SessionKeys.LastRoute);
        const lastUser = this.sessionStorage.retrieve(SessionKeys.LastUser);

        this.sessionStorage.clear(SessionKeys.LastRoute);
        this.sessionStorage.clear(SessionKeys.LastUser);

        if (
          lastUser?.toUpperCase() === username.toUpperCase() &&
          lastRoute &&
          !['/login', '/authentication/login'].includes(lastRoute)
        ) {
          this.router.navigateByUrl(lastRoute);
        } else {
          this.router.navigate(['/home']);
        }
        return;
      }

      this.showErrorMessage(this.obj_loginResponse?.result);
    });
  }

  /* ---------------- UI HELPERS ---------------- */

  showValidationMsg() {
    this.str_message.set("Enter Username and Password");
    this.str_warningState.set("danger");
    this.int_flagDisableMessage.set(true);
  }

  showErrorMessage(msg: string) {
    if (!msg) return;
    this.str_message.set(msg);
    this.str_warningState.set("danger");
    this.int_flagDisableMessage.set(true);
  }


  // =========================
// DEBOUNCE UTILITY
// =========================
private _debounce(func: Function, delay = 500) {
  let debounceTimer: any;

  return (...args: any[]) => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      func.apply(this, args);
    }, delay);
  };
}

// =========================
// DEBOUNCED METHOD (USED IN HTML)
// =========================
checkUserID_Delayed = this._debounce(() => this.checkUserID(), 500);

// =========================
// ACTUAL USER CHECK
// =========================
checkUserID(): void {
  const username = this.usernameSignal();

  if (!username) {
    this.userResponse.set(null);
    return;
  }

  this.authService.checkUserStatus(username).subscribe({
    next: (res: any) => {
      if (!res || res.error) {
        this.errorHandling.checkError(res?.error?.status || 500);
        return;
      }

      if (Array.isArray(res.result) && res.status === 'success') {
        this.userResponse.set(res);
        this.str_message.set('');
        this.str_warningState.set('');
      } else {
        this.userResponse.set(null);
        this.int_flagDisableMessage.set(true);
        this.str_message.set(`Enter Valid ${this.str_messagePlaceholder}`);
        this.str_warningState.set('danger');
      }
    },
    error: () => {
      this.errorHandling.checkError(500);
    }
  });
}


  /* ---------------- CHANGE PASSWORD ---------------- */

  onChangePassword() {
    this.bln_isPopupOpened = true;

    const dialogRef = this.dialog.open(ChangePasswordModalComponent, {
      data: { UserID: this.loginForm.value.username },
      width: '500px',
      maxWidth: '95vw',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(() => {
      this.bln_isPopupOpened = false;
    });
  }




disableLoginBtn(): boolean {
  return false;
}

showValidateBtn(): boolean {
  return false;
}

ldapSubmit() {
  this.nldapSubmit(); // call existing method
}

disablePWDBtn(): boolean {
  return false;
}

}

